import { branding } from '@guridosdrones/shared';
import { Menu, X } from 'lucide-react';

import logo from '@/assets/logo.png';
import { useHeader } from '@/components/header/use-header';
import { ThemeToggle } from '@/components/theme-toggle';
import { getWhatsAppLink, WHATSAPP_MESSAGES } from '@/utils/whatsapp';

export function Header() {
  const { isMenuOpen, hasBackground, scrollToSection, toggleMenu } = useHeader();

  const courseNavigation = [
    { name: 'Mapeamento', href: '#mapping' },
    { name: 'Módulos', href: '#course-details' },
    { name: 'Instrutor', href: '#instructor' },
    { name: 'Contato', href: '#contact' },
  ];

  const openWhatsApp = () => {
    window.open(getWhatsAppLink(WHATSAPP_MESSAGES.HEADER), '_blank', 'noopener,noreferrer');
  };

  return (
    <header className="fixed top-0 right-0 left-0 z-50 px-4 pt-2 sm:px-6 sm:pt-2.5 lg:px-8">
      <nav
        className={`relative mx-auto max-w-7xl border px-2 py-1.5 transition-all duration-500 sm:px-3 sm:py-2 ${
          hasBackground
            ? 'rounded-sm border-border/60 bg-background/85 shadow-[0_30px_90px_-55px_rgba(0,0,0,0.42)] backdrop-blur-2xl dark:border-white/10 dark:bg-background/80'
            : 'rounded-sm border-border/40 bg-background/80 shadow-[0_24px_80px_-52px_rgba(0,0,0,0.15)] backdrop-blur-xl dark:border-white/10 dark:bg-black/25'
        }`}
      >
        {/* Top accent line */}
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r transition-opacity duration-500 ${
            hasBackground
              ? 'from-transparent via-primary/50 to-transparent opacity-60'
              : 'from-primary/0 via-primary/40 to-primary/0 opacity-100'
          }`}
        />

        <div className="flex h-12 items-center justify-between gap-3 sm:h-14">
          {/* Logo */}
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <button
              type="button"
              onClick={() => scrollToSection('#start')}
              aria-label="Ir para o topo da página"
              className="group flex min-w-0 cursor-pointer items-center gap-2.5 px-1.5 py-1 text-left transition-all duration-300"
            >
              <img
                src={logo}
                alt={branding.brandName}
                className="h-9 w-9 shrink-0 object-contain sm:h-10 sm:w-10"
              />
              <div className="min-w-0">
                <h1
                  className={`truncate text-sm leading-tight font-heading font-semibold tracking-[-0.03em] transition-colors duration-500 sm:text-base ${
                    hasBackground ? 'text-foreground' : 'text-foreground dark:text-white'
                  }`}
                >
                  <span className="font-medium uppercase">{branding.brandNamePart1}</span>{' '}
                  <span className="font-extrabold uppercase text-primary drop-shadow-[0_0_18px_rgba(16,185,129,0.35)] transition-colors duration-500">
                    {branding.brandNamePart2}
                  </span>
                </h1>
                <p
                  className={`hidden font-mono text-[0.6rem] font-semibold tracking-[0.2em] uppercase md:block transition-colors duration-500 ${
                    hasBackground
                      ? 'text-muted-foreground'
                      : 'text-muted-foreground dark:text-white/50'
                  }`}
                >
                  Pulverização Agrícola
                </p>
              </div>
            </button>
          </div>

          {/* Desktop Nav */}
          <div className="hidden items-center gap-1 lg:flex">
            <div
              className={`flex h-9 items-center gap-0.5 rounded-sm border p-1 backdrop-blur-xl transition-all duration-500 ${
                hasBackground
                  ? 'border-border/50 bg-card/60 shadow-sm dark:border-white/10 dark:bg-white/5'
                  : 'border-border/40 bg-card/60 dark:border-white/10 dark:bg-white/5'
              }`}
            >
              {courseNavigation.map((item) => (
                <button
                  type="button"
                  key={item.name}
                  onClick={() => scrollToSection(item.href)}
                  className={`flex h-7 items-center rounded-sm px-4 font-mono text-xs font-medium tracking-wide uppercase transition-all ${
                    hasBackground
                      ? 'text-muted-foreground hover:bg-muted/80 hover:text-foreground dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white'
                      : 'text-muted-foreground hover:bg-foreground/10 hover:text-foreground dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white'
                  }`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden items-center gap-3 lg:flex">
            <div
              className={`flex h-9 items-center rounded-sm border p-1 backdrop-blur-xl transition-all duration-500 ${
                hasBackground
                  ? 'border-border/50 bg-card/60 text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white'
                  : 'border-border/40 bg-card/60 text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white'
              }`}
            >
              <ThemeToggle />
            </div>
            <button
              type="button"
              onClick={openWhatsApp}
              className={`h-9 rounded-sm px-6 font-mono text-xs font-bold tracking-widest uppercase transition-all duration-500 ${
                hasBackground
                  ? 'border border-primary/30 bg-primary/15 text-primary hover:border-primary/50 hover:bg-primary/25'
                  : 'border border-primary/40 bg-primary/15 text-primary hover:border-primary/70 hover:bg-primary/25'
              }`}
            >
              Matricule-se
            </button>
          </div>

          {/* Mobile Actions */}
          <div className="flex items-center gap-2 lg:hidden">
            <div
              className={`flex h-9 items-center rounded-sm border p-1 backdrop-blur-xl transition-all duration-500 ${
                hasBackground
                  ? 'border-border/50 bg-card/60 text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white'
                  : 'border-border/40 bg-card/60 text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white'
              }`}
            >
              <ThemeToggle />
            </div>

            <button
              type="button"
              onClick={toggleMenu}
              className={`relative flex h-9 w-9 items-center justify-center rounded-sm backdrop-blur-xl transition-all duration-500 ${
                hasBackground
                  ? 'border border-border/50 bg-card/60 text-muted-foreground hover:bg-muted/80 hover:text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white'
                  : 'border border-border/40 bg-card/60 text-muted-foreground hover:bg-muted/80 hover:text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white'
              }`}
              aria-label="Alternar menu"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              <Menu
                className={`absolute h-5 w-5 transition-all duration-300 ${isMenuOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
              />
              <X
                className={`absolute h-5 w-5 transition-all duration-300 ${isMenuOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}
              />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          id="mobile-menu"
          className={`overflow-hidden transition-all duration-300 lg:hidden ${
            isMenuOpen
              ? `mt-3 max-h-112 pt-4 pb-2 opacity-100 ${
                  hasBackground
                    ? 'border-t border-border/50 dark:border-white/10'
                    : 'border-t border-border/40 dark:border-white/10'
                }`
              : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex flex-col gap-1">
            {courseNavigation.map((item) => (
              <button
                type="button"
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                className={`block w-full rounded-sm px-4 py-3 text-left font-mono text-xs font-medium tracking-wide uppercase transition-colors ${
                  hasBackground
                    ? 'text-foreground hover:bg-muted/80 dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-white'
                    : 'text-foreground/70 hover:bg-foreground/10 hover:text-foreground dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-white'
                }`}
              >
                {item.name}
              </button>
            ))}

            <div className="mt-4 px-2 pb-2">
              <button
                type="button"
                onClick={openWhatsApp}
                className={`h-12 w-full rounded-sm font-mono text-sm font-bold tracking-widest uppercase transition-all ${
                  hasBackground
                    ? 'border border-primary/30 bg-primary/15 text-primary hover:bg-primary/25'
                    : 'border border-primary/40 bg-primary/15 text-primary hover:bg-primary/25'
                }`}
              >
                Garantir Minha Vaga
              </button>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
